'use client';

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import type { TourStep } from '@/lib/tour-config';

interface TourContextValue {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  start: (steps: TourStep[]) => void;
  next: () => void;
  prev: () => void;
  complete: () => void;
  skip: () => void;
  registerOnEnd: (fn: () => void) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const onEndRef = useRef<(() => void) | null>(null);

  const isActive = currentStep >= 0 && currentStep < steps.length;

  const start = useCallback((newSteps: TourStep[]) => {
    setSteps(newSteps);
    setCurrentStep(0);
  }, []);

  const next = useCallback(
    () => setCurrentStep((s) => (s + 1 < steps.length ? s + 1 : s)),
    [steps.length],
  );

  const prev = useCallback(
    () => setCurrentStep((s) => (s > 0 ? s - 1 : s)),
    [],
  );

  const end = useCallback(() => {
    setCurrentStep(-1);
    onEndRef.current?.();
  }, []);

  const registerOnEnd = useCallback((fn: () => void) => {
    onEndRef.current = fn;
  }, []);

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps: steps.length,
        start,
        next,
        prev,
        complete: end,
        skip: end,
        registerOnEnd,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}
